import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { CurrencySelect } from '@/components/ui/currency-select';
import { useDepartments } from '@/hooks/finance/useDepartments';
import { useCostPositionsForDepartment } from '@/hooks/finance/useDepartmentCostPositions';
import { useCreatePaymentRequest, useSubmitPaymentRequest } from '@/hooks/finance/usePaymentRequests';
import { CreatePaymentRequestItemInput } from '@/types/database/payment-requests';
import { formatCurrency, DEFAULT_CURRENCY } from '@/lib/currency';
import { toast } from 'sonner';
import { BusinessUnitSelect } from '@/components/finance/BusinessUnitSelect';
import { DEFAULT_BUSINESS_UNIT, BusinessUnit } from '@/constants/businessUnits';

interface LineItem extends CreatePaymentRequestItemInput {
  id: string;
}

const NewPaymentRequestPage = () => {
  const navigate = useNavigate();
  const { data: departments } = useDepartments();
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const { data: filteredCategories } = useCostPositionsForDepartment(selectedDepartmentId);
  const createRequest = useCreatePaymentRequest();
  const submitRequest = useSubmitPaymentRequest();

  const [formData, setFormData] = useState({
    department_id: '',
    category_id: '',
    title: '',
    description: '',
    vendor_name: '',
    vendor_bank_name: '',
    vendor_bank_account: '',
    vendor_bank_branch: '',
    payment_method: 'bank_transfer',
    preferred_payment_date: '',
    vat_applicable: false,
    currency: DEFAULT_CURRENCY,
    business_unit: DEFAULT_BUSINESS_UNIT as BusinessUnit,
  });

  const [items, setItems] = useState<LineItem[]>([
    { id: '1', description: '', quantity: 1, unit_price: 0, amount: 0 },
  ]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // When department changes, update the selected department for filtering and reset category
    if (field === 'department_id') {
      setSelectedDepartmentId(value as string || null);
      setFormData(prev => ({ ...prev, department_id: value as string, category_id: '' }));
    }
  };

  const handleItemChange = (id: string, field: keyof LineItem, value: string | number) => {
    setItems(prev => prev.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          updated.amount = Number(updated.quantity) * Number(updated.unit_price);
        }
        return updated;
      }
      return item;
    }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit_price: 0,
      amount: 0
    }]);
  };

  const removeItem = (id: string) => {
    if (items.length > 1) {
      setItems(prev => prev.filter(item => item.id !== id));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.amount, 0);
    const vatAmount = formData.vat_applicable ? subtotal * 0.15 : 0;
    const total = subtotal + vatAmount;
    return { subtotal, vatAmount, total };
  };

  const { subtotal, vatAmount, total } = calculateTotals();

  const handleSaveDraft = async () => {
    if (!formData.title) {
      toast.error('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await createRequest.mutateAsync({
        ...formData,
        department_id: formData.department_id || undefined,
        category_id: formData.category_id || undefined,
        amount: subtotal,
        vat_amount: vatAmount,
        total_amount: total,
        payment_method: formData.payment_method as 'cash' | 'bank_transfer' | 'check' | undefined,
        items: items.filter(i => i.description && i.amount > 0).map(({ id, ...item }) => item),
      });
      navigate('/finance/accounting/payment-requests');
    } catch (error) {
      console.error('Failed to save draft:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitForApproval = async () => {
    if (!formData.title || !formData.department_id) {
      toast.error('Please fill in all required fields including department');
      return;
    }

    if (items.every(i => !i.description || i.amount <= 0)) {
      toast.error('Please add at least one line item');
      return;
    }

    setIsSubmitting(true);
    try {
      const request = await createRequest.mutateAsync({
        ...formData,
        department_id: formData.department_id || undefined,
        category_id: formData.category_id || undefined,
        amount: subtotal,
        vat_amount: vatAmount,
        total_amount: total,
        payment_method: formData.payment_method as 'cash' | 'bank_transfer' | 'check' | undefined,
        items: items.filter(i => i.description && i.amount > 0).map(({ id, ...item }) => item),
      });
      await submitRequest.mutateAsync(request.id);
      navigate('/finance/accounting/payment-requests');
    } catch (error) {
      console.error('Failed to submit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">New Payment Request</h1>
            <p className="text-muted-foreground">Create a new payment request for approval</p>
          </div>
        </div>

        <div className="grid gap-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle>Request Details</CardTitle>
              <CardDescription>Basic information about the payment request</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="department">Department *</Label>
                  <Select
                    value={formData.department_id}
                    onValueChange={(v) => handleInputChange('department_id', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select department" />
                    </SelectTrigger>
                    <SelectContent>
                      {departments?.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select
                    value={formData.category_id}
                    onValueChange={(v) => {
                      const cat = filteredCategories?.find((c) => c.id === v);
                      const defaultBU = (cat as any)?.default_business_unit as BusinessUnit | undefined;
                      setFormData((prev) => ({
                        ...prev,
                        category_id: v,
                        business_unit: defaultBU || prev.business_unit,
                      }));
                    }}
                    disabled={!formData.department_id}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={formData.department_id ? "Select category" : "Select department first"} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredCategories?.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Business Unit *</Label>
                <BusinessUnitSelect
                  value={formData.business_unit}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, business_unit: v }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Brief description of the payment"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Additional Notes</Label>
                <Textarea
                  id="description"
                  placeholder="Any additional information"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Vendor Information */}
          <Card>
            <CardHeader>
              <CardTitle>Receiver Account Information</CardTitle>
              <CardDescription>Details about who will receive the payment</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="vendor_name">Vendor Name</Label>
                <Input
                  id="vendor_name"
                  placeholder="Name of vendor or payee"
                  value={formData.vendor_name}
                  onChange={(e) => handleInputChange('vendor_name', e.target.value)}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_name">Bank Name</Label>
                  <Input
                    id="vendor_bank_name"
                    placeholder="e.g., CBE"
                    value={formData.vendor_bank_name}
                    onChange={(e) => handleInputChange('vendor_bank_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_account">Account Number</Label>
                  <Input
                    id="vendor_bank_account"
                    placeholder="Account number"
                    value={formData.vendor_bank_account}
                    onChange={(e) => handleInputChange('vendor_bank_account', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vendor_bank_branch">Branch</Label>
                  <Input
                    id="vendor_bank_branch"
                    placeholder="Branch name"
                    value={formData.vendor_bank_branch}
                    onChange={(e) => handleInputChange('vendor_bank_branch', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Line Items</CardTitle>
                  <CardDescription>Itemized breakdown of the payment</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="currency" className="text-sm text-muted-foreground">Currency</Label>
                  <CurrencySelect
                    value={formData.currency}
                    onValueChange={(v) => handleInputChange('currency', v)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={item.id} className="flex gap-3 items-start">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                      />
                    </div>
                    <div className="w-20">
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity || ''}
                        onChange={(e) => handleItemChange(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32">
                      <Input
                        type="number"
                        placeholder="Unit Price"
                        value={item.unit_price || ''}
                        onChange={(e) => handleItemChange(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                      />
                    </div>
                    <div className="w-32 flex items-center justify-end font-medium">
                      {formatCurrency(item.amount, formData.currency)}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={items.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <Button variant="outline" onClick={addItem}>
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>

              <Separator />

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(subtotal, formData.currency)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={formData.vat_applicable}
                      onCheckedChange={(v) => handleInputChange('vat_applicable', v)}
                    />
                    <Label>VAT (15%)</Label>
                  </div>
                  <span className="text-sm">{formatCurrency(vatAmount, formData.currency)}</span>
                </div>
                <Separator />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>{formatCurrency(total, formData.currency)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Details */}
          <Card>
            <CardHeader>
              <CardTitle>Payment Details</CardTitle>
              <CardDescription>How and when should this be paid</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="payment_method">Payment Method</Label>
                  <Select
                    value={formData.payment_method}
                    onValueChange={(v) => handleInputChange('payment_method', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="preferred_payment_date">Preferred Payment Date</Label>
                  <Input
                    id="preferred_payment_date"
                    type="date"
                    value={formData.preferred_payment_date}
                    onChange={(e) => handleInputChange('preferred_payment_date', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={handleSaveDraft} disabled={isSubmitting}>
              Save as Draft
            </Button>
            <Button onClick={handleSubmitForApproval} disabled={isSubmitting}>
              Submit for Approval
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewPaymentRequestPage;
